import React, { useCallback, useMemo, useState } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { Grid, Typography, Divider, Link, OutlinedInput } from '@material-ui/core'
import { useParams } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import Svg, { ICON } from '_components/svg'
import HistoryLog from '_models/history-log'
import { RateServiceModal } from 'src/components/service-order/modals/RateServiceModal'
import MoreDetailsModal from '_components/modal/more-details-modal'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import { NEXT_URL } from '_config/environment'
import { getMessagesByServiceOrderId } from '_modules/messages/actions'
import CardMedia from '_components/card-media'
import { Attachments } from '_components/refera-ui'
import * as GenericModal from '_components/modal/generic-modal'
import { separateAttachmentsByType } from '_utils/helpers'
import Theme from '@refera/ui-core'

import useStyles from './styles'
import { Button } from '@mui/material'

const resolveServiceOrderId = rawServiceOrderId => {
  if (rawServiceOrderId === null || rawServiceOrderId === undefined) return null

  if (typeof rawServiceOrderId === 'object') {
    return (
      rawServiceOrderId?.get?.('id') ||
      rawServiceOrderId?.id ||
      rawServiceOrderId?.get?.('serviceOrder') ||
      rawServiceOrderId?.serviceOrder ||
      null
    )
  }

  return rawServiceOrderId
}

const HistoryItem = ({ item, isLastItem, className }) => {
  const styles = useStyles({ isLastItem })
  const dispatch = useDispatch()
  const [moreDetailsModalOpen, setMoreDetailsModalOpen] = useState(false)
  const [warrantyModalOpen, setWarrantyModalOpen] = useState(false)
  const [rateServiceModalOpen, setRateServiceModalOpen] = useState(false)
  const [selectedRatingId, setSelectedRatingId] = useState(null)
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const { serviceOrderId: serviceOrderIdFromRoute } = useParams()
  const rawServiceOrderId = useMemo(
    () => item?.serviceOrder || serviceOrderIdFromRoute || serviceOrder?.get('id') || null,
    [item?.serviceOrder, serviceOrderIdFromRoute, serviceOrder?.get('id')]
  )
  const serviceOrderId = useMemo(
    () => resolveServiceOrderId(rawServiceOrderId),
    [rawServiceOrderId]
  )

  const arrayOfOptionsSelectedReprovedService = item.optionsSelectedReprovedService
    ? item.optionsSelectedReprovedService.replace(/'/g, '').slice(1, -1).split(', ')
    : []

  const problemDescriptionText = useMemo(() => {
    const raw = String(item?.problemDescription ?? '')
    return raw
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/\s*p\s*>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .trim()
  }, [item?.problemDescription])

  const isCompleteCanceledServiceOrder = item?.logType === 'complete_canceled_service_order'
  const isWarrantyOpening = item?.logType === 'warranty_opening'
  const isWarrantyFinished = item?.logType === 'warranty_finished'
  const isIntermediaryBudgetRefused =
    item?.logType === 'intermediary_budget_refused' && item?.reasonsReproveService
  const isServiceOrderCanceled = item?.title === 'Chamado cancelado'
  const hasCancelDetails = isServiceOrderCanceled && item?.cancelDetails

  const iconTypeToRender = useMemo(() => {
    if (isWarrantyFinished) return ICON.STAR
    if (isWarrantyOpening && !Object.values(ICON).includes(item?.iconType)) return ICON.SHIELD_TICK
    return item?.iconType
  }, [isWarrantyFinished, isWarrantyOpening, item?.iconType])

  const iconBackgroundColorToRender = useMemo(() => {
    if (isWarrantyFinished) return Theme.Colors.Primary.Base
    return item?.iconBackgroundColor
  }, [isWarrantyFinished, item?.iconBackgroundColor])

  const shouldShowMoreDetailsButton = useMemo(() => {
    return (
      isWarrantyOpening ||
      isWarrantyFinished ||
      isCompleteCanceledServiceOrder ||
      isIntermediaryBudgetRefused ||
      item.optionsSelectedReprovedService ||
      item?.aiResponse ||
      hasCancelDetails
    )
  }, [
    isCompleteCanceledServiceOrder,
    isIntermediaryBudgetRefused,
    isWarrantyFinished,
    isWarrantyOpening,
    item,
    hasCancelDetails,
  ])

  const moreDetailsButtonLabel = useMemo(() => {
    if (isIntermediaryBudgetRefused) return 'Ver mais detalhes'
    return 'Mais detalhes'
  }, [isIntermediaryBudgetRefused])

  const modalTitle = useMemo(() => {
    const { logType = '', moreDetailsModalTitle = '', title = '' } = item || {}

    if (logType === 'ai_analysis') return 'Mais Detalhes'
    if (logType === 'complete_canceled_service_order') return 'Mais detalhes'
    if (title === 'Chamado cancelado' && hasCancelDetails)
      return 'Mais detalhes do cancelamento do chamado'
    if (moreDetailsModalTitle) return moreDetailsModalTitle
    return 'Motivo da reprovação'
  }, [item, hasCancelDetails])

  const warrantyModalTitle = useMemo(() => {
    if (isWarrantyFinished) return 'Detalhes da conclusão da garantia'
    return 'Detalhes da garantia'
  }, [isWarrantyFinished])

  const descriptionTitle = useMemo(() => {
    const { logType = '' } = item || {}

    if (logType === 'ai_analysis') return 'Descrição do serviço'
    return 'Descrição'
  }, [item])

  const fetchMoreDetails = useCallback(() => {
    if (!isCompleteCanceledServiceOrder || !serviceOrderId) return Promise.resolve([])
    return dispatch(
      getMessagesByServiceOrderId(serviceOrderId, {
        origin: 'complete_canceled_service_order',
        log_event_id: item?.id,
      })
    )
  }, [dispatch, isCompleteCanceledServiceOrder, item?.id, serviceOrderId])

  const renderMoreDetailsContent = useCallback(
    ({ data, error }) => {
      if (isCompleteCanceledServiceOrder) {
        if (error) {
          return <Typography>Não foi possível carregar os detalhes.</Typography>
        }
        const messages = Array.isArray(data) ? data : []

        return (
          <Grid style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            {messages.length > 0 ? (
              messages.map(msg => (
                <div key={msg.id} style={{ width: '100%' }}>
                  <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                    Detalhes da conclusão do chamado
                  </Typography>
                  <OutlinedInput
                    fullWidth
                    multiline
                    value={msg.message || ''}
                    inputProps={{
                      readOnly: true,
                      tabIndex: -1,
                      style: { userSelect: 'none' },
                    }}
                    tabIndex={-1}
                    onFocus={e => e.target.blur()}
                    onMouseDown={e => e.preventDefault()}
                  />

                  {msg.attachments && msg.attachments.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Typography style={{ fontWeight: 600, marginBottom: 8 }}>Anexos</Typography>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {msg.attachments.map(att => (
                          <div
                            key={att.id || att.file}
                            style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                          >
                            {att?.file ? (
                              (() => {
                                const fileUrl = String(att.file || '')
                                const withoutQuery = fileUrl.split('?')[0].toLowerCase()
                                const isPdf = withoutQuery.endsWith('.pdf')

                                if (isPdf) {
                                  return <CardMedia file={fileUrl} showOnly />
                                }

                                return (
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Abrir anexo em nova aba"
                                    style={{ display: 'inline-block' }}
                                  >
                                    <CardMedia file={fileUrl} showOnly lockView />
                                  </a>
                                )
                              })()
                            ) : (
                              <div
                                style={{
                                  height: 80,
                                  width: 80,
                                  borderRadius: 4,
                                  backgroundColor: '#EAEAEA',
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <Typography>Nenhum detalhe encontrado.</Typography>
            )}
          </Grid>
        )
      }

      if (hasCancelDetails) {
        return (
          <Grid style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
              Detalhes do cancelamento do chamado
            </Typography>
            <OutlinedInput
              fullWidth
              multiline
              value={item?.cancelDetails || ''}
              inputProps={{
                readOnly: true,
                tabIndex: -1,
                style: { userSelect: 'none' },
              }}
              tabIndex={-1}
              onFocus={e => e.target.blur()}
              onMouseDown={e => e.preventDefault()}
            />
          </Grid>
        )
      }

      return (
        <>
          <Grid className={styles.modalContent}>
            {arrayOfOptionsSelectedReprovedService.length > 0 && (
              <>
                <Typography className={styles.titleModal}>Relacionado a</Typography>
                <Typography className={styles.textModal}>
                  {arrayOfOptionsSelectedReprovedService
                    .map(option => {
                      if (item?.logType === 'service_order_refused') {
                        return item?.optionsSelectedReprovedService
                      }
                      if (option === 'servicos_executados') {
                        return 'Serviços executados'
                      }
                      if (option === 'nota_fiscal_emitida') {
                        return 'Nota fiscal emitida'
                      }
                      if (option === 'fotos_anexadas') {
                        return 'Fotos anexadas'
                      }
                      return 'Outros motivos'
                    })
                    .join(', ')}
                  .
                </Typography>
              </>
            )}
          </Grid>

          {item?.logType === 'ai_analysis' && (
            <Grid className={styles.modalContent}>
              <Typography className={styles.titleModal}>Problema</Typography>
              <Typography className={styles.descriptionModal}>{problemDescriptionText}</Typography>
            </Grid>
          )}

          <Grid className={styles.modalContent}>
            <Typography className={styles.titleModal}>{descriptionTitle}</Typography>
            <Typography className={styles.descriptionModal}>
              {item?.reasonsReproveService}
            </Typography>
          </Grid>

          {item?.logType === 'ai_analysis' && (
            <>
              <Grid className={styles.modalContent}>
                <Typography className={styles.titleModal}>Resposta da IA</Typography>
                <Typography className={styles.descriptionModal}>{item?.aiResponse}</Typography>
              </Grid>
              <Grid className={styles.modalContent}>
                <Typography className={styles.titleModal}>Porquê não ajustou</Typography>
                <Typography className={styles.descriptionModal}>{item?.tradesmanReason}</Typography>
              </Grid>
            </>
          )}
        </>
      )
    },
    [
      arrayOfOptionsSelectedReprovedService,
      descriptionTitle,
      isCompleteCanceledServiceOrder,
      hasCancelDetails,
      item,
      styles.descriptionModal,
      styles.modalContent,
      styles.textModal,
      styles.titleModal,
    ]
  )

  const warrantyText = item?.relatedMessage?.get ? item.relatedMessage.get('message') : ''
  const warrantyAttachments = useMemo(() => {
    if (!item?.relatedMessage?.get) return []
    const attachments = item.relatedMessage.get('messageAttachment')
    if (!attachments?.toJS) return []
    return attachments.toJS()
  }, [item])

  const isFilePDF = useCallback(file => {
    const url = (file?.file || '').toLowerCase().split('?')[0]
    return url.endsWith('.pdf')
  }, [])

  const { media: attachmentsMedia, pdf: attachmentsPdf } = useMemo(
    () => separateAttachmentsByType(warrantyAttachments, isFilePDF),
    [warrantyAttachments, isFilePDF]
  )

  return (
    <>
      <MoreDetailsModal
        open={moreDetailsModalOpen}
        onClose={() => setMoreDetailsModalOpen(false)}
        title={modalTitle}
        fetchData={isCompleteCanceledServiceOrder ? fetchMoreDetails : null}
        fetchKey={isCompleteCanceledServiceOrder ? `${serviceOrderId}-${item?.id}` : undefined}
        renderContent={renderMoreDetailsContent}
      />

      <RateServiceModal
        key="rateServiceModal"
        open={rateServiceModalOpen}
        onClose={() => setRateServiceModalOpen(false)}
        ratingDetails={selectedRatingId}
        logType={item?.logType}
        fallbackFeedback={item?.reasonsReproveService}
      />

      <GenericModal.Root
        open={warrantyModalOpen}
        onClose={() => setWarrantyModalOpen(false)}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <GenericModal.TitleModal title={warrantyModalTitle} />
        <Divider style={{ marginBottom: 20 }} />
        <GenericModal.Content
          style={{ alignItems: 'stretch', display: 'flex', flexDirection: 'column', gap: 24 }}
        >
          <GenericModal.InputDisabled text={warrantyText || 'Sem detalhes'} label="Detalhes" />

          {attachmentsMedia.length > 0 && (
            <div style={{ width: '100%' }}>
              <Attachments
                label="Anexos"
                readOnly
                downloadable
                files={attachmentsMedia}
                accept={{
                  'image/jpeg': [
                    '.jpeg',
                    '.png',
                    '.jpg',
                    '.bmp',
                    '.webp',
                    '.mkv',
                    '.mp4',
                    '.mov',
                    '.avi',
                    '.m4v',
                    '.mpg',
                    '.mpeg',
                    '.wmv',
                    '.webm',
                  ],
                }}
                onDrop={() => {}}
                onRemoveItem={() => {}}
                onItemClick={index => {
                  const url = attachmentsMedia?.[index]?.file
                  if (!url) return
                  window.open(url, '_blank', 'noopener,noreferrer')
                }}
              />
            </div>
          )}

          {attachmentsPdf.length > 0 && (
            <div style={{ width: '100%' }}>
              <Attachments
                label="Arquivos PDF"
                content="files"
                readOnly
                downloadable
                files={attachmentsPdf}
                accept={{ 'application/pdf': ['.pdf'] }}
                multiple={false}
                onDrop={() => {}}
                onRemoveItem={() => {}}
                onItemClick={index => {
                  const url = attachmentsPdf?.[index]?.file
                  if (!url) return
                  window.open(url, '_blank', 'noopener,noreferrer')
                }}
              />
            </div>
          )}
        </GenericModal.Content>
        <GenericModal.Actions>
          <GenericModal.ButtonFullBlue onClick={() => setWarrantyModalOpen(false)}>
            Fechar
          </GenericModal.ButtonFullBlue>
        </GenericModal.Actions>
      </GenericModal.Root>
      <Grid className={classnames(styles.history, className)}>
        <Grid className={styles.icon} style={{ backgroundColor: iconBackgroundColorToRender }}>
          {Object.values(ICON).includes(iconTypeToRender) && (
            <Svg
              type={iconTypeToRender}
              className={classnames(
                styles.timelineIcon,
                iconTypeToRender === ICON.BUDGET_SENT && styles.budgetSentTimelineIcon,
                (iconTypeToRender === ICON.RESCHEDULED_VISIT ||
                  iconTypeToRender === ICON.SCHEDULED_VISIT) &&
                  styles.visitTimelineIcon
              )}
              style={{ color: item?.iconColor }}
            />
          )}
        </Grid>
        {!isLastItem && <Divider orientation="vertical" className={styles.divider} />}
        <Grid className={styles.content}>
          <Typography className={styles.title}>{item?.title}</Typography>
          <Typography className={styles.description}>{item?.message}</Typography>
          {item?.url && (
            <Link
              className={styles.description}
              href={item?.url}
              color="inherit"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              Ver mais detalhes &gt;{' '}
            </Link>
          )}
          {shouldShowMoreDetailsButton && (
            <Button
              className={styles.button}
              onClick={() => {
                if (isWarrantyOpening || isWarrantyFinished) {
                  setWarrantyModalOpen(true)
                  return
                }
                setMoreDetailsModalOpen(true)
              }}
            >
              {moreDetailsButtonLabel}
            </Button>
          )}
          {(item.logType === 'tenant_approved_service' ||
            item.logType === 'tenant_repproved_service') && (
            <Button
              className={styles.button}
              onClick={() => {
                if (item.userActiveProfile === 'refera') {
                  setSelectedRatingId(item.rating)
                  setRateServiceModalOpen(true)
                } else {
                  window.open(
                    `${NEXT_URL}/${serviceOrder?.get('agency')?.get('slug')}/service-rating?token=${
                      serviceOrder?.uuid
                    }&rating=${item.rating}`,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              }}
            >
              Ver mais detalhes
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  )
}

HistoryItem.propTypes = {
  item: PropTypes.instanceOf(HistoryLog).isRequired,
  isLastItem: PropTypes.bool,
  className: PropTypes.string,
}

HistoryItem.defaultProps = {
  isLastItem: false,
  className: '',
}

export default React.memo(HistoryItem)
